import { useCallback, useEffect, useRef, useState } from "react"
import { MapCommon } from "./MapCommon"
import { MapEvaluation } from "./MapEvaluation"
import { MapFeatures } from "./MapFeatures"
import { MapPcsGroup } from "./MapPcsGroup"
import { MapPecExam } from "./MapPecExam"
import { isArrayEmpty, isObjectNull } from "../../../helpers/ObjectHelpers"
import MapPropertiesService from "../services/MapPropertiesService"
import MapApiRepository from "../repositories/MapApiRepository"
import { useSearchParams } from "react-router"
import MapParamsService, { IMapParamsService } from "../services/MapParamsService"
import { MapMarkerInfoWindow } from "./MapMarkerInfoWindow"
import { MapPilotageDetailsDialog } from "../dialogs/MapPilotageDetailsDialog"
import { MapCertificateLocations } from "./MapCertificateLocations"
import { MapPecFairwayAreas } from "./MapPecFairwayAreas"
import { MapPecApplicationFairwayAreas } from "./MapPecApplicationFairwayAreas"
import { isRequestAborted } from "../../../helpers/ApiHelpers"
import { isMobile } from "react-device-detect"
import { MapMarkerInfoPanel } from "./MapMarkerInfoPanel"
import { MapMarkers } from "./MapMarkers"
import { useMapId, useShowInfo, useSetSelectShipPosition } from "../hooks/useMapStore"
import { MapPilotCertificateCoastalSegment } from "./MapPilotCertificateCoastalSegment"
import { useWarningNotification } from 'components/hooks/useAppStore';

interface MapContentProps {
    showSettings?: boolean,
    showSearch?: boolean,
    showFeatures?: boolean,
    showAis?: boolean,
    showPcs?: boolean,
    showSeamap?: boolean,
    showCertificateLocations?: boolean,
    showPilotageShipPosition?: boolean,
    pilotageId?: number,
    incidentPilotId?: number,
    shipId?: number,
    focusOnSegmentId?: number,
    pcsGroupId?: number | null,
    pecExam?: any,
    positionType?: string | null,
    position?: any | null
    featureCollection?: any | null,
    evaluation?: any | null,
    selectedPosition: google.maps.LatLng | null,
    autoUpdateSearchParams: boolean,
    pecId?: number | null,
    pecApplicationId?: number | null,
    pecLatitude?: number | null,
    pecLongitude?: number | null,

    setSelectedPosition: (e: google.maps.LatLng) => void,
    onClick?: (e: any) => void,
    onChange?: (e: any) => void,
    onInitFeatures?: (e: number[]) => void,
    onFeatureMouseOver?: (e: number) => void,
    onFeatureMouseOut?: () => void,
    onFeatureClick?: (e: any) => void,
}

export const MapContent = ({
    showSettings = true,
    showSearch = false,
    showFeatures = false,
    showAis = false,
    showPcs = false,
    showSeamap = false,
    showCertificateLocations = false,
    showPilotageShipPosition = true,
    pilotageId = 0,
    incidentPilotId = 0,
    shipId = 0,
    focusOnSegmentId = 0,
    pcsGroupId = null,
    pecExam = null,
    positionType = null,
    onClick,
    onChange,
    position = null,
    autoUpdateSearchParams,
    pecId = null,
    pecApplicationId = 0,
    pecLatitude = 0,
    pecLongitude = 0,
    featureCollection,
    evaluation,
    selectedPosition,
    setSelectedPosition,
    onInitFeatures,
    onFeatureMouseOver,
    onFeatureMouseOut,
    onFeatureClick,
}: MapContentProps) => {

    const addWarningNotification = useWarningNotification();

    const mapId = useMapId();
    const stateShowInfo = useShowInfo();
    const setSelectShipPosition = useSetSelectShipPosition();

    const [searchParams, setSearchParams] = useSearchParams();

    const [paramsService, setParamsService] = useState<IMapParamsService | null>(null);

    const mapClickTypes = MapPropertiesService.clickTypes;

    const [pilotage, setPilotage] = useState<any | null>(null);
    const [showMultiSelect, setShowMultiSelect] = useState(false);
    const [hasPilotageLoaded, setHasPilotageLoaded] = useState(pilotageId === 0);
    const [hasShipPositionLoaded, setHasShipPositionLoaded] = useState(shipId === 0);

    const pcsGroupRef: { current: any } = useRef(null);

    let abortController = new AbortController();

    const fetchShipPositionAsync = async (id: number, fromPilotage = false) => {

        abortController.abort();
        abortController = new AbortController();

        const response: any = await MapApiRepository.getShipPositionsAsync([id], abortController.signal);
        if (response.ok) {
            const data = await response.json();
            if (!isArrayEmpty(data)) {
                setSelectShipPosition(data[0]);
            }
        } else {
            setSelectShipPosition(null);
            if (!isRequestAborted(response)) {
                addWarningNotification("Kunne ikke finne fart&oslash;yets AIS posisjon.");
            }
        }

        setHasShipPositionLoaded(true);

        if (!fromPilotage) return;
        setHasPilotageLoaded(true);
    }

    const fetchPilotageAsync = async (id: number) => {
        const response: any = await MapApiRepository.getReadOnlyPilotageByIdAsync(id);
        if (response.ok) {
            const data = await response.json();

            setPilotage(data);

            if (showPilotageShipPosition)
                fetchShipPositionAsync(data.shipId, true);

        } else {
            setPilotage(null);

            setHasShipPositionLoaded(true);
            setHasPilotageLoaded(true);
        }
    }

    const handleShowInfoMessage = (data: any) => {
        if (!paramsService) return;

        let mmsi = 0;
        let locationId = 0;

        if (data) {
            switch (data.type) {
                case "ship":
                    mmsi = data.mmsi;
                    break;
                case "location":
                    locationId = data.id;
                    break;
                default:
                    break;
            }
        }
        if (!isMobile) return;

        paramsService.onMmsiChange(mmsi);
        paramsService.onLocationIdChange(locationId);
    }

    const handleOnChange = useCallback((changes: any) => {
        if (onChange)
            onChange(changes);
    }, [onChange]);

    const handleOnInitFeatures = useCallback((ids: number[]) => {
        if (!onInitFeatures) return;
        onInitFeatures(ids);
    }, [onInitFeatures]);

    const handleOnFeatureMouseOver = useCallback((id: number) => {
        if (onFeatureMouseOver)
            onFeatureMouseOver(id);
    }, [onFeatureMouseOver]);

    const handleOnFeatureMouseOut = useCallback(() => {
        if (onFeatureMouseOut)
            onFeatureMouseOut();
    }, [onFeatureMouseOut]);

    const handleOnFeatureClick = useCallback((e: any) => {
        if (onFeatureClick)
            onFeatureClick(e);
    }, [onFeatureClick]);

    const onMultiSelectCancelClick = useCallback(() => {
        pcsGroupRef.current?.onMultiSelectCancel();
    }, []);

    const onMultiSelectOkClick = useCallback(() => {
        pcsGroupRef.current?.onMultiSelectOk();
    }, []);

    useEffect(() => {
        handleShowInfoMessage(stateShowInfo);
    }, [stateShowInfo]);
    
    useEffect(() => {
        const paramsService = new MapParamsService(autoUpdateSearchParams, searchParams, setSearchParams);
        if (showSeamap) {
            paramsService._showSeamap = showSeamap;
        }
        setParamsService(paramsService);

    }, []);

    useEffect(() => {
        if (pilotageId === 0) return;
        fetchPilotageAsync(pilotageId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pilotageId]);

    useEffect(() => {
        if (shipId === 0 || pilotageId > 0) return;
        fetchShipPositionAsync(shipId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipId]);

    useEffect(() => {
        setShowMultiSelect(showPcs);
    }, [showPcs]);

    return (
        
        paramsService &&
        <>
            <MapMarkerInfoWindow/>
            <MapMarkerInfoPanel/>
            <MapPilotageDetailsDialog/>

            <MapCommon
                pilotageId={pilotageId}
                shipId={shipId}
                showCertificateLocations={showCertificateLocations}
                showSearch={showSearch}
                showSettings={showSettings}
                showMultiSelect={showMultiSelect}
                showAis={showAis}
                hasPilotageLoaded={hasPilotageLoaded}
                hasShipPositionLoaded={hasShipPositionLoaded}
                pilotage={pilotage}
                pecExam={pecExam}
                position={position}
                positionType={positionType ?? mapClickTypes.Marker}
                selectedPosition={selectedPosition}
                setSelectedPosition={setSelectedPosition}
                canClickPosition={!isObjectNull(onClick)}
                onMultiSelectCancelClick={onMultiSelectCancelClick}
                onMultiSelectOkClick={onMultiSelectOkClick}
                paramsService={paramsService}
            />

            <MapMarkers
                showAis={showAis}
                pilotageId={pilotageId}
                shipId={shipId}
            />

            {
                (pecExam) &&
                <MapPecExam
                    pecExam={pecExam}
                />
            }

            {
                (showFeatures) &&
                <MapFeatures
                    featureCollection={featureCollection}
                    onChanges={handleOnChange}
                    onFeatureMouseOver={handleOnFeatureMouseOver}
                    onFeatureMouseOut={handleOnFeatureMouseOut}
                    onFeatureClick={handleOnFeatureClick}
                    focusOnSegmentId={focusOnSegmentId}
                />
            }

            {
                (showPcs) &&
                <MapPcsGroup
                    ref={pcsGroupRef}
                    pcsGroupId={pcsGroupId!}
                    onFeatureClick={handleOnFeatureClick}
                    onInitFeatures={handleOnInitFeatures}
                />
            }

            {
                (showCertificateLocations) &&
                <MapCertificateLocations
                    incidentPilotId={incidentPilotId}
                    isActive={true}
                />
            }

            {
                (evaluation) &&
                <MapEvaluation
                    mapId={mapId}
                    evaluation={evaluation}
                />
            }

            {
                (pecId) &&
                <MapPecFairwayAreas
                    pecId={pecId}
                    pecLatitude={pecLatitude}
                    pecLongitude={pecLongitude}
                />
            }

            {
                (pecApplicationId) &&
                <MapPecApplicationFairwayAreas
                    pecApplicationId={pecApplicationId}
                    pecLatitude={pecLatitude}
                    pecLongitude={pecLongitude}
                />
            }

            <MapPilotCertificateCoastalSegment />

        </>
    )
}
